/**
 * Map model tuning (M3 plan R9, §2.7): `src/data/reach-maps.json`, Zod-validated here. Colours stay CSS hex strings
 * (the DOM panels use them as-is); `colorNumber` turns one into a three.js-ready 0xRRGGBB, cached.
 */
import { z } from 'zod'
import mapsJson from '../../data/reach-maps.json'

const hex = z.string().regex(/^#[0-9A-Fa-f]{6}$/, 'expected #RRGGBB')
const positive = z.number().positive()

export const reachMapsSchema = z.object({
  comment: z.string().optional(),
  galaxy: z.object({
    nodeRadius: z.object({ charted: positive, rumoured: positive }),
    labelMax: z.number().int().min(0),
    avoidDanger: positive,
    colors: z.object({
      region: z.object({ gate: hex, mid: hex, deep: hex, rim: hex }),
      danger: z
        .array(z.tuple([z.number().min(0), hex]))
        .min(1)
        .refine((stops) => stops.every((s, i) => i === 0 || s[0] > stops[i - 1][0]), 'danger stops must ascend'),
      online: hex,
      offline: hex,
      reboot: hex,
      route: hex,
    }),
  }),
  system: z.object({
    logR0M: positive,
    radius: positive,
    yScale: z.number().min(0).max(1),
    iconSize: z.object({ station: positive, relay: positive, poi: positive, body: positive, laneRing: positive, field: positive, star: positive }),
  }),
  camera: z
    .object({
      fovDeg: z.number().min(10).max(120),
      minDistance: positive,
      maxDistance: positive,
      orbitSpeed: positive,
      padOrbitSpeed: positive,
      zoomStep: z.number().gt(1),
      maxPitchDeg: z.number().gt(0).lt(90),
      focusRate: positive,
    })
    .refine((c) => c.maxDistance > c.minDistance, 'maxDistance must exceed minDistance'),
})

export type ReachMapsConfig = z.infer<typeof reachMapsSchema>

/** Parses and validates reach-maps.json (throws a ZodError naming the bad field). */
export function loadReachMapsConfig(raw: unknown = mapsJson): ReachMapsConfig {
  return reachMapsSchema.parse(raw)
}

const colorCache = new Map<string, number>()

/** '#RRGGBB' → 0xRRGGBB, cached so repeated view builds do not re-parse. */
export function colorNumber(hexColor: string): number {
  let n = colorCache.get(hexColor)
  if (n === undefined) {
    n = Number.parseInt(hexColor.slice(1), 16)
    colorCache.set(hexColor, n)
  }
  return n
}

/** The danger overlay colour: the last stop whose threshold is at or below `danger` (the first stop below them all). */
export function dangerColor(danger: number, cfg: ReachMapsConfig): string {
  const stops = cfg.galaxy.colors.danger
  let color = stops[0][1]
  for (let i = 1; i < stops.length; i++) {
    if (danger >= stops[i][0]) color = stops[i][1]
  }
  return color
}
